// Pestaña Factoring: operaciones de financiamiento registradas (Iter 355)
import { useCallback, useEffect, useState } from 'react';
import api from '../../lib/api';
import { fmtFecha } from '../../lib/utils';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Loader2, Undo2 } from 'lucide-react';
import { toast } from 'sonner';

const CLP = (v) => (v || 0).toLocaleString('es-CL');

export function FactoringTab({ refreshKey, onChanged }) {
  const [ops, setOps] = useState(null);
  const load = useCallback(() => {
    api.get('/conciliacion-bancaria/factoring').then((r) => setOps(r.data.operaciones || [])).catch(() => toast.error('Error cargando factoring'));
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const deshacer = async (op) => {
    if (!window.confirm(`¿Deshacer la operación de factoring ${op.contraparte.nombre} (${op.depositos.length} depósitos por $${CLP(op.anticipo_total)})? Los depósitos vuelven a pendiente. No se toca Alegra.`)) return;
    try {
      await api.delete(`/conciliacion-bancaria/factoring/${op.id}`);
      toast.success('Operación deshecha'); load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  if (!ops) return <div className="py-8 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div>;
  return (
    <div className="space-y-3" data-testid="factoring-tab">
      <p className="text-sm text-muted-foreground">Operaciones de factoring / financiamiento: los depósitos del anticipo se concilian en grupo contra el pago ya registrado en Alegra. La <b>liquidación</b> (intereses, comisión, excedente) se registra cuando llega la cartola con el cierre.</p>
      {ops.length === 0 && <p className="p-6 text-sm text-muted-foreground" data-testid="factoring-empty">Sin operaciones registradas. En Movimientos, usa el botón 💰 en un depósito de la empresa de factoring.</p>}
      {ops.map((op) => (
        <Card key={op.id} data-testid={`factoring-op-${op.id}`}><CardContent className="space-y-2 p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="font-semibold">{op.contraparte.nombre} <span className="text-xs text-muted-foreground">{op.contraparte.identificacion}</span></p>
              <p className="text-xs text-muted-foreground">Anticipo {op.fecha_anticipo} · {op.depositos.length} depósitos · pago Alegra {op.alegra_payment_id ? `#${op.alegra_payment_id} (${op.alegra_payment_fecha})` : '—'} · registrado por {op.created_by}</p>
            </div>
            <div className="flex items-center gap-2">
              <Badge className={op.estado === 'liquidada' ? 'bg-emerald-100 text-emerald-700' : 'bg-violet-100 text-violet-700'} data-testid={`factoring-estado-${op.id}`}>{op.estado === 'liquidada' ? 'Liquidada' : 'Anticipo recibido · liquidación pendiente'}</Badge>
              {op.estado !== 'liquidada' && <Button variant="ghost" size="sm" className="text-orange-600" onClick={() => deshacer(op)} data-testid={`btn-deshacer-factoring-${op.id}`}><Undo2 className="mr-1 h-3.5 w-3.5" /> Deshacer</Button>}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4 text-sm">
            <div className="rounded border p-2"><p className="text-[10px] uppercase text-muted-foreground">Anticipo recibido</p><p className="font-bold">${CLP(op.anticipo_total)}</p></div>
            <div className="rounded border p-2"><p className="text-[10px] uppercase text-muted-foreground">Facturas cedidas</p><p className="font-bold">${CLP(op.facturas.reduce((s, f) => s + (f.total || 0), 0))}</p></div>
            <div className="rounded border p-2"><p className="text-[10px] uppercase text-muted-foreground">Saldo facturas en Alegra</p><p className={`font-bold ${op.saldo_facturas ? 'text-amber-600' : 'text-emerald-600'}`} data-testid={`factoring-saldo-${op.id}`}>${CLP(op.saldo_facturas)}</p></div>
            <div className="rounded border p-2"><p className="text-[10px] uppercase text-muted-foreground">Por liquidar (intereses + excedente)</p><p className="font-bold">${CLP(op.saldo_facturas)}</p></div>
          </div>
          <div className="grid gap-2 text-xs md:grid-cols-2">
            <div><p className="font-medium">Facturas cedidas</p>{op.facturas.map((f) => <p key={f.alegra_id}>#{f.numero} {f.cliente_nombre} · total ${CLP(f.total)} · saldo ${CLP(f.saldo_actual)} · vence {f.fecha_vencimiento || '—'} · {f.estado_factura}</p>)}</div>
            <div><p className="font-medium">Depósitos</p>{op.depositos.map((d) => <p key={d.mov_id}>{fmtFecha(d.fecha)} · {d.detalle} · ${CLP(d.monto)}</p>)}</div>
          </div>
          {op.nota && <p className="text-xs text-muted-foreground">Nota: {op.nota}</p>}
        </CardContent></Card>
      ))}
    </div>
  );
}
